// Admin-Sicht auf alle Frage-Threads.
//
// Datenschutz-Modell:
//   - Patient-Identität anonymisiert (8-Zeichen-Pseudonym, nicht zurück auflösbar)
//   - Frage-Texte + Rezept-Bezug sichtbar (für Rezept-Verbesserung)
//   - Antworten der Praxis NICHT sichtbar (Patient/Praxis-Sphäre)
//   - Filter nach Praxis und Rezept
//
// Ziel: Verstehen, wo Rezepte unklar sind, häufige Folgefragen auslösen.

import { useEffect, useState } from 'react';
import { listAdminFragen, type AdminFrageView } from '../api';

export default function FragenAdmin() {
  const [threads, setThreads] = useState<AdminFrageView[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filterPraxis, setFilterPraxis] = useState<string>('');
  const [filterRezept, setFilterRezept] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'alle' | 'offen' | 'beantwortet' | 'geschlossen'>('alle');
  const [search, setSearch] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await listAdminFragen();
      setThreads(r.threads);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Unique-Listen für Filter-Dropdowns
  const praxenList = Array.from(
    new Map(threads.map((t) => [t.praxis_id, t.praxis_name])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));
  const rezepteList = Array.from(new Set(threads.map((t) => t.rezept_id).filter(Boolean) as string[]))
    .sort();

  const filtered = threads.filter((t) => {
    if (filterPraxis && t.praxis_id !== filterPraxis) return false;
    if (filterRezept && t.rezept_id !== filterRezept) return false;
    if (filterStatus !== 'alle' && t.status !== filterStatus) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !t.titel.toLowerCase().includes(q) &&
        !t.erste_frage.toLowerCase().includes(q) &&
        !t.praxis_name.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Patienten-Fragen</h1>
          <p className="page-sub">
            Alle Frage-Threads pseudonymisiert. Patient-Identität nicht zurückverfolgbar, Antworten
            der Praxis aus Datenschutzgründen nicht sichtbar — nur die Patientenfrage + Kontext.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={reload}>↻ Neu laden</button>
      </div>

      {err && <div className="card" style={{ color: '#D9534F', marginBottom: 16 }}>Fehler: {err}</div>}

      {/* Filter-Toolbar */}
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Frage oder Praxis suchen…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 240px', padding: '8px 12px', border: '1px solid #DDE9E8', borderRadius: 6, fontSize: 14 }}
        />
        <select
          value={filterPraxis}
          onChange={(e) => setFilterPraxis(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #DDE9E8', borderRadius: 6, fontSize: 14 }}
        >
          <option value="">Alle Praxen</option>
          {praxenList.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <select
          value={filterRezept}
          onChange={(e) => setFilterRezept(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #DDE9E8', borderRadius: 6, fontSize: 14 }}
        >
          <option value="">Alle Rezepte</option>
          {rezepteList.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          style={{ padding: '8px 10px', border: '1px solid #DDE9E8', borderRadius: 6, fontSize: 14 }}
        >
          <option value="alle">Alle Status</option>
          <option value="offen">Offen</option>
          <option value="beantwortet">Beantwortet</option>
          <option value="geschlossen">Geschlossen</option>
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#5F6B68' }}>
          {loading ? 'Lädt…' : `${filtered.length} von ${threads.length}`}
        </div>
      </div>

      {threads.length === 0 && !loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
          <h3 style={{ margin: '8px 0' }}>Noch keine Fragen</h3>
          <p className="page-sub">Sobald Patienten Fragen an ihre Praxen stellen, erscheinen sie hier.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((t) => <FrageCard key={t.id} thread={t} />)}
        </div>
      )}

      <div className="info-card" style={{ marginTop: 24 }}>
        <h4>Was hier sichtbar ist und warum</h4>
        Patient-Identität: <strong>pseudonymisiert</strong> (z. B. <code>a3f2-…</code>). Selbst der
        System-Admin kann nicht zurückverfolgen, wer die Frage gestellt hat.<br />
        Frage-Text + Rezept-Bezug: <strong>sichtbar</strong>, um häufige Fragen zu Rezepten zu erkennen
        und die Beratungs-Inhalte gezielt zu verbessern.<br />
        Antworten der Praxis: <strong>NICHT sichtbar</strong>. Diese gehören rechtlich zur Sphäre
        Patient/Praxis (medizinische Beratung).
      </div>
    </>
  );
}

function FrageCard({ thread }: { thread: AdminFrageView }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1D1D1B' }}>{thread.titel}</div>
          <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#5F6B68', marginTop: 4, flexWrap: 'wrap' }}>
            <span>🏥 {thread.praxis_name}</span>
            {thread.rezept_id && <span>📋 Rezept: <code>{thread.rezept_id}</code></span>}
            <span>👤 Patient: <code>{thread.patient_pseudonym}</code></span>
            <span>📅 {new Date(thread.created_at).toLocaleString('de-DE')}</span>
          </div>
        </div>
        <StatusBadge status={thread.status} hasAnswer={thread.hat_antwort_von_praxis} />
      </div>

      <div style={{
        background: '#F4FAFA',
        borderLeft: '3px solid #00A099',
        padding: '10px 14px',
        borderRadius: 4,
        fontSize: 14,
        color: '#1D1D1B',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
      }}>
        {thread.erste_frage || '(leere Frage)'}
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: '#5F6B68', display: 'flex', justifyContent: 'space-between' }}>
        <span>{thread.anzahl_nachrichten} Nachricht{thread.anzahl_nachrichten === 1 ? '' : 'en'} im Thread</span>
        <span>
          {thread.hat_antwort_von_praxis
            ? '✅ Praxis hat geantwortet (Inhalt nicht sichtbar)'
            : '⏳ Noch nicht beantwortet'}
        </span>
      </div>
    </div>
  );
}

function StatusBadge({ status, hasAnswer }: { status: AdminFrageView['status']; hasAnswer: boolean }) {
  const cfg = {
    offen: { bg: '#FFF4E5', fg: '#B85C00', label: hasAnswer ? 'Folgefrage' : 'Offen' },
    beantwortet: { bg: '#E8F5E9', fg: '#2E7D32', label: 'Beantwortet' },
    geschlossen: { bg: '#ECEFF1', fg: '#455A64', label: 'Geschlossen' },
  }[status];
  return (
    <span style={{
      background: cfg.bg, color: cfg.fg, fontSize: 11, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: 0.5, padding: '4px 10px', borderRadius: 999,
      alignSelf: 'flex-start', whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}
